import React from 'react';
import { Droplets, AlertTriangle, Wrench, TrendingUp } from 'lucide-react';

const PredictiveMaintenance: React.FC = () => {
  // Mock data - in real app this would come from floor sensors
  const maintenanceData = {
    activeAlerts: [
      { 
        id: 1, 
        type: 'Leak Detection', 
        location: 'Kitchen Area - Zone 3',
        severity: 'high',
        timestamp: '2024-03-20 08:45 AM',
        description: 'Moisture levels exceeding threshold'
      },
      { 
        id: 2, 
        type: 'Wear Pattern', 
        location: 'Main Lobby',
        severity: 'medium',
        timestamp: '2024-03-20 09:15 AM',
        description: 'Accelerated floor wear detected'
      },
      { 
        id: 3, 
        type: 'Structural Load', 
        location: 'Conference Room B',
        severity: 'low',
        timestamp: '2024-03-20 10:30 AM',
        description: 'Unusual pressure distribution'
      }
    ],
    moistureReadings: [
      { zone: 'Kitchen Area', level: 82, status: 'Alert', trend: 'increasing' },
      { zone: 'Bathroom Zone 1', level: 45, status: 'Normal', trend: 'stable' },
      { zone: 'Utility Room', level: 38, status: 'Normal', trend: 'decreasing' },
      { zone: 'Break Room', level: 52, status: 'Warning', trend: 'increasing' }
    ],
    maintenancePredictions: [
      { 
        area: 'Main Entrance',
        issue: 'Floor Wear',
        timeframe: '2-3 weeks',
        confidence: 85
      },
      { 
        area: 'South Corridor',
        issue: 'Joint Stress',
        timeframe: '1-2 months',
        confidence: 72
      },
      { 
        area: 'Conference Zone',
        issue: 'Surface Degradation',
        timeframe: '3-4 months',
        confidence: 68
      }
    ],
    pressurePoints: [
      { location: 'Reception', intensity: 85, status: 'High Traffic' },
      { location: 'Elevator Lobby', intensity: 92, status: 'Critical' },
      { location: 'Cafeteria Entry', intensity: 78, status: 'Moderate' },
      { location: 'Emergency Exit', intensity: 45, status: 'Normal' }
    ]
  };

  const getSeverityColor = (severity: string) => {
    switch (severity.toLowerCase()) {
      case 'high':
        return 'bg-red-100 text-red-800';
      case 'medium':
        return 'bg-yellow-100 text-yellow-800';
      case 'low':
        return 'bg-green-100 text-green-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'alert':
        return 'text-red-500';
      case 'warning':
        return 'text-yellow-500';
      case 'normal':
        return 'text-green-500';
      default:
        return 'text-gray-500';
    }
  };

  return (
    <div className="p-6">
      <h2 className="text-2xl font-bold mb-6">Predictive Maintenance</h2>
      
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center mb-2">
            <AlertTriangle className="text-red-500 mr-2" />
            <h3 className="text-lg font-semibold">Active Alerts</h3>
          </div>
          <p className="text-3xl font-bold">{maintenanceData.activeAlerts.length}</p>
          <p className="text-gray-500">Issues detected</p>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center mb-2">
            <Droplets className="text-blue-500 mr-2" />
            <h3 className="text-lg font-semibold">Moisture Alerts</h3>
          </div>
          <p className="text-3xl font-bold">2</p>
          <p className="text-gray-500">Zones above threshold</p>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center mb-2">
            <Wrench className="text-purple-500 mr-2" />
            <h3 className="text-lg font-semibold">Predicted Issues</h3>
          </div>
          <p className="text-3xl font-bold">{maintenanceData.maintenancePredictions.length}</p>
          <p className="text-gray-500">Next 3 months</p>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center mb-2">
            <TrendingUp className="text-green-500 mr-2" />
            <h3 className="text-lg font-semibold">Pressure Points</h3>
          </div>
          <p className="text-3xl font-bold">2</p>
          <p className="text-gray-500">Critical areas</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-xl font-semibold mb-4">Active Alerts</h3>
          <div className="space-y-4">
            {maintenanceData.activeAlerts.map((alert) => (
              <div key={alert.id} className="border-b pb-4">
                <div className="flex justify-between items-start mb-2">
                  <div>
                    <span className={`px-2 py-1 rounded-full text-xs ${getSeverityColor(alert.severity)}`}>
                      {alert.severity.toUpperCase()}
                    </span>
                    <h4 className="font-medium mt-2">{alert.type}</h4>
                  </div>
                  <span className="text-sm text-gray-500">{alert.timestamp}</span>
                </div>
                <p className="text-sm text-gray-600">{alert.location}</p>
                <p className="text-sm text-gray-500 mt-1">{alert.description}</p>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-xl font-semibold mb-4">Moisture Readings</h3>
          <div className="space-y-4">
            {maintenanceData.moistureReadings.map((reading, index) => (
              <div key={index} className="flex items-center justify-between">
                <div className="flex-1">
                  <p className="font-medium">{reading.zone}</p>
                  <p className={`text-sm ${getStatusColor(reading.status)}`}>
                    {reading.status} • {reading.trend}
                  </p>
                </div>
                <div className="flex items-center">
                  <div className="w-32 bg-gray-200 rounded-full h-2 mr-3">
                    <div
                      className={`rounded-full h-2 ${
                        reading.level > 75 ? 'bg-red-500' : 
                        reading.level > 50 ? 'bg-yellow-500' : 'bg-green-500'
                      }`}
                      style={{ width: `${reading.level}%` }}
                    ></div>
                  </div>
                  <span className="font-medium">{reading.level}%</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-xl font-semibold mb-4">Maintenance Predictions</h3>
          <div className="space-y-4">
            {maintenanceData.maintenancePredictions.map((prediction, index) => (
              <div key={index} className="border-b pb-4 last:border-b-0">
                <div className="flex justify-between items-start">
                  <div>
                    <h4 className="font-medium">{prediction.area}</h4>
                    <p className="text-sm text-gray-600">{prediction.issue}</p>
                  </div>
                  <div className="text-right">
                    <p className="font-medium">{prediction.timeframe}</p>
                    <p className="text-sm text-gray-500">{prediction.confidence}% confidence</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-xl font-semibold mb-4">Pressure Point Analysis</h3>
          <div className="space-y-4">
            {maintenanceData.pressurePoints.map((point, index) => (
              <div key={index} className="flex items-center justify-between">
                <div>
                  <p className="font-medium">{point.location}</p>
                  <p className="text-sm text-gray-500">{point.status}</p>
                </div>
                <div className="flex items-center">
                  <div className="w-32 bg-gray-200 rounded-full h-2 mr-3">
                    <div
                      className={`rounded-full h-2 ${
                        point.intensity > 90 ? 'bg-red-500' :
                        point.intensity > 75 ? 'bg-yellow-500' : 'bg-green-500'
                      }`}
                      style={{ width: `${point.intensity}%` }}
                    ></div>
                  </div>
                  <span className="font-medium">{point.intensity}%</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PredictiveMaintenance; 